import React from "react";
import { CallViewsSummary } from "../../../types/call-views.types";
import { formatNumber } from "../../../utils/formatters";

interface SummaryCardsProps {
  summary: CallViewsSummary;
}

interface CardProps {
  label: string;
  value: string;
  testId: string;
  ariaLabel: string;
}

const Card: React.FC<CardProps> = ({ label, value, testId, ariaLabel }) => (
  <div
    className="bg-white rounded-lg border border-neutral-200 p-4 shadow-sm"
    data-testid={testId}
    aria-label={ariaLabel}
  >
    <div className="text-xs font-medium text-neutral-500 uppercase mb-1">
      {label}
    </div>
    <div
      className="text-2xl font-semibold text-neutral-900"
      data-testid={`${testId}-value`}
    >
      {value}
    </div>
  </div>
);

const formatDecimal = (value: number): string =>
  new Intl.NumberFormat("pl-PL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);

export const SummaryCards: React.FC<SummaryCardsProps> = ({ summary }) => {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      role="region"
      aria-label="Podsumowanie przeglądania naborów"
    >
      <Card
        label="Łączna liczba odsłon"
        value={formatNumber(summary.totalViews)}
        testId="metric-total-views"
        ariaLabel={`Łączna liczba odsłon: ${formatNumber(summary.totalViews)}`}
      />
      <Card
        label="Aktywni użytkownicy"
        value={formatNumber(summary.activeUsers)}
        testId="metric-active-users"
        ariaLabel={`Aktywni użytkownicy: ${formatNumber(summary.activeUsers)}`}
      />
      <Card
        label="Średnia odsłon / użytkownik"
        value={formatDecimal(summary.avgViewsPerUser)}
        testId="metric-avg-per-user"
        ariaLabel={`Średnia odsłon na użytkownika: ${formatDecimal(summary.avgViewsPerUser)}`}
      />
      <Card
        label="Mediana odsłon / użytkownik"
        value={formatDecimal(summary.medianViewsPerUser)}
        testId="metric-median-per-user"
        ariaLabel={`Mediana odsłon na użytkownika: ${formatDecimal(summary.medianViewsPerUser)}`}
      />
      <Card
        label="Śr. odsłon / użytkownik / dzień"
        value={formatDecimal(summary.avgViewsPerUserPerDay)}
        testId="metric-avg-per-user-day"
        ariaLabel={`Średnia odsłon na użytkownika na dzień: ${formatDecimal(summary.avgViewsPerUserPerDay)}`}
      />
      <Card
        label="Śr. odsłon / użytkownik / tydzień"
        value={formatDecimal(summary.avgViewsPerUserPerWeek)}
        testId="metric-avg-per-user-week"
        ariaLabel={`Średnia odsłon na użytkownika na tydzień: ${formatDecimal(summary.avgViewsPerUserPerWeek)}`}
      />
    </div>
  );
};

export default SummaryCards;
